import classNames from "classnames";
import { OverlayTrigger, Tooltip } from "react-bootstrap";

import { EntityWithUrl } from "@/utils/sos/utilities";
import { HashPreservingLink, HashPreservingLinkProps } from "./HashPreservingLink";
import { SosEntityLink } from "./SosEntityLink";

export interface SosEntityHashLinkProps
  extends
    Omit<React.HTMLAttributes<HTMLAnchorElement>, "id" | "children" | "href">,
    Pick<HashPreservingLinkProps, "fromHash"> {
  /**
   * Optional URL path, defaults to current page
   */
  urlPath?: string;

  /**
   * Optional tooltip content to show when hovering over the link. If not provided, no tooltip will be shown.
   */
  tooltipContent?: string;

  type: EntityWithUrl;
  id: number;

  /**
   * Optional suffix to append to the hash id. This is useful when you want to link to a specific rendered element of
   * the target entity, like a specific line item
   * */
  idSuffix?: string;

  label: React.ReactNode;
}

/**
 * Renders a link that performs an internal navigation to the specified page, with the hash id set to that of a specific
 * entity, based on the provided entity type and id.
 *
 * NOTE: The target of this link should either be currently rendered on the page, or be rendered with `<HashNavScrollTarget>`
 * to ensure the page scrolls to the correct position when the target component loads.
 */
export const SOSEntityHashLink: React.FC<SosEntityHashLinkProps> = ({
  urlPath,
  tooltipContent,
  type,
  id,
  idSuffix,
  label,
  ...linkProps
}) => {
  const href = `${urlPath ?? ""}#${getSosEntityHashId(type, id, idSuffix)}`;

  return (
    <>
      <OverlayTrigger overlay={<Tooltip>{tooltipContent}</Tooltip>}>
        <HashPreservingLink to={href} {...linkProps} className={classNames("me-1", linkProps.className)}>
          {label}
        </HashPreservingLink>
      </OverlayTrigger>
      <SosEntityLink type={type} id={id} label={""} />
    </>
  );
};

/**
 * Gets the SOS entity hash id for a specific entity type and id, optionally appending a suffix. The suffix is useful
 * when you want to link to a specific rendered element of the target entity, like a specific line item.
 */
export function getSosEntityHashId(type: EntityWithUrl, id: number, suffix?: string): string {
  return `${type}-${id.toFixed()}${suffix ? `-${suffix}` : ""}`;
}
